import classNames from 'classnames'
import { Carousel } from 'components/carousels/Carousel'
import { Icon } from 'components/common/Icon'
import { Popover } from 'components/common/Popover'
import { ReleaseCard } from 'components/release-calendar/ReleaseCard'
import { ReleaseCardSkeleton } from 'components/release-calendar/ReleaseCardSkeleton'
import { Releases } from 'components/release-calendar/ReleasesList'
import routes from 'constants/routes'
import {
  useAuth,
  useComingReleases,
  useFutureReleases,
  usePastReleases,
} from 'contexts'
import useTranslation from 'next-translate/useTranslation'
import { ContainerElement, PropsWithClassName } from 'types'
import { range } from 'utils/arrays'

export function AllTab() {
  const { t } = useTranslation('release_calendar')
  const { user } = useAuth()
  const { data: pastReleases } = usePastReleases({
    pagination: { pageSize: user ? 20 : 12 },
  })
  const { data: futureReleases, isFetchedAfterMount: isFetchedFutureReleases } =
    useFutureReleases({ pagination: { pageSize: user ? 100 : 10 } })
  const {
    data: comingSoonReleases,
    isFetchedAfterMount: isFetchedComingReleases,
  } = useComingReleases()

  return (
    <>
      <div className={classNames('pb-6 pt-10 sm:py-6 bg-warm-50')}>
        {/* this carousel should be shown only on mobile because the images of the first two releases are loaded with priority */}
        {/* they should not be shown on desktop because the lcp on desktop is probably the banner image, which is prioritized */}
        <PastReleasesCarousel className='sm:hidden'>
          {!pastReleases.list.length && <ReleasesSkeletons />}

          {Boolean(pastReleases.list.length) &&
            pastReleases.list.map((release, index) => (
              <ReleaseCard
                key={release.id}
                {...release}
                keepAspectRatio
                priority={index < 2}
              />
            ))}
        </PastReleasesCarousel>

        <PastReleasesCarousel className='hidden sm:block'>
          {!pastReleases.list.length && <ReleasesSkeletons />}

          {Boolean(pastReleases.list.length) &&
            pastReleases.list.map(release => (
              <ReleaseCard
                key={release.id}
                {...release}
                keepAspectRatio
                className='hidden sm:block'
              />
            ))}
        </PastReleasesCarousel>
      </div>

      {(!isFetchedFutureReleases || Boolean(futureReleases.list.length)) && (
        <>
          <div className='mb-3 mt-4 hidden px-6 sm:mt-8 md:mb-4 md:mt-8 md:px-12 [@media(min-width:369px)]:block'>
            <div className='mx-auto max-w-[90rem]'>
              <h4 className='font-body text-xl font-medium tracking-normal'>
                {t('upcoming_releases')}
              </h4>
            </div>
          </div>

          <section className='mb-6 flex w-full flex-wrap items-center px-6 sm:mb-10 md:px-12'>
            <Releases
              mappedReleases={futureReleases.mapped}
              shouldDisplayDate
            />
          </section>
        </>
      )}

      {(!isFetchedComingReleases ||
        Boolean(comingSoonReleases.list.length)) && (
        <>
          <div className='bg-warm-50 px-6 pb-5 pt-6 sm:pt-8 md:px-12'>
            <div className='mx-auto flex max-w-[90rem] items-center space-x-2'>
              <h4 className='font-body text-lg font-medium tracking-normal sm:text-xl'>
                {t('coming_soon')}
              </h4>

              <Popover content={<p>{t('release_date_tba')}</p>} side='top'>
                <Icon
                  name='question-filled'
                  filled
                  className='text-secondary-default'
                  size={20}
                />
              </Popover>
            </div>
          </div>

          <section className='flex w-full flex-wrap items-center bg-warm-50 px-6 pb-8 sm:mt-0 md:px-12'>
            <Releases
              mappedReleases={comingSoonReleases.mapped}
              shouldDisplayDate={false}
            />
          </section>
        </>
      )}
    </>
  )
}

function PastReleasesCarousel({ children, className }: ContainerElement) {
  const { t } = useTranslation('release_calendar')
  const { user } = useAuth()

  return (
    <Carousel
      title={t('recent_releases')}
      className={className}
      headerClassName='!mb-4 min-h-10'
      titleClassName='font-body text-lg font-medium tracking-normal sm:text-xl'
      viewAllLabel={user ? t('common:view_all') : undefined}
      viewAllHref={user ? `${routes.releaseCalendar.base}?tab=past` : undefined}
    >
      {children}
    </Carousel>
  )
}

function ReleasesSkeletons({ className }: PropsWithClassName) {
  return range(0, 6).map(i => (
    <ReleaseCardSkeleton key={i} keepAspectRatio className={className} />
  ))
}
